import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  CATEGORIES,
  CATEGORY_LABELS,
  isBlogCategory,
} from '../../../blog/blog-categories';
import { BLOG_POSTS } from '../../../blog/blog-posts-token';
import { isHubIndexable } from '../../../blog/hub-indexing';
import { PageCtaComponent } from '../../../content/page-cta/page-cta.component';
import { BRAND_SUFFIX, PageTitleService } from '../../../page-title';
import { RouteUiDataService } from '../../../route-ui-data';
import { SEOService } from '../../../seo.service';
import { BlogPostCardComponent } from '../blog-post-card/blog-post-card.component';

@Component({
  selector: 'app-blog-topic',
  imports: [RouterLink, BlogPostCardComponent, PageCtaComponent],
  templateUrl: './blog-topic.component.html',
  styleUrl: './blog-topic.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlogTopicComponent {
  private pageTitle = inject(PageTitleService);
  private seo = inject(SEOService);
  private routeUiData = inject(RouteUiDataService);
  private allPosts = inject(BLOG_POSTS);

  readonly category = input.required<string>();

  readonly isValid = computed(() => isBlogCategory(this.category()));

  readonly label = computed(
    () => CATEGORY_LABELS[this.category()] ?? this.category(),
  );

  readonly posts = computed(() =>
    this.isValid()
      ? this.allPosts.filter((p) => p.category === this.category())
      : [],
  );

  constructor() {
    effect(() => {
      if (!isHubIndexable(this.posts().length)) {
        this.seo.setRobotsNoindex();
      } else {
        this.seo.clearRobots();
      }

      if (this.isValid()) {
        const descriptor = CATEGORIES.find((c) => c.slug === this.category());
        const heading = this.label();
        const fullTitle = `${heading} | Bass Guitar Blog${BRAND_SUFFIX}`;
        this.pageTitle.setPageTitle(fullTitle, {
          title: heading,
          ...(descriptor ? { lead: descriptor.description } : {}),
        });
        this.routeUiData.setBreadcrumbOverride([
          { name: 'Home', path: '/' },
          { name: 'Blog', path: '/blog' },
          { name: heading },
        ]);
      } else {
        const fullTitle = `Topic not found | Bass Guitar Blog${BRAND_SUFFIX}`;
        this.pageTitle.setPageTitle(fullTitle, { title: 'Topic not found' });
        this.routeUiData.setBreadcrumbOverride([
          { name: 'Home', path: '/' },
          { name: 'Blog', path: '/blog' },
          { name: 'Topic not found' },
        ]);
      }
    });
  }
}
